import { Box, Center, Group, Paper, SegmentedControl, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { DeviceMobileIcon, MonitorIcon } from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { ButtonRow } from '../../components/global/ButtonRow/ButtonRow.tsx';
import { EventPreview as EventPreviewPanel } from '../../components/global/EventPreview/EventPreview.tsx';
import { FlowStep } from '../../components/layout/FlowStep.tsx';
import { Button } from '../../components/ui/Button/Button.tsx';
import { usePromoterItemQuery } from '../../data/promoter/usePromoterItemQuery.ts';
import { useVenueItemQuery } from '../../data/venues/useVenueItemQuery.ts';
import { env } from '../../env.ts';
import { usePublishEventsMutation } from '../../helpers/publishEvents.ts';
import { useEventOptions } from '../../hooks/useEventOptions/useEventOptions.ts';
import { useI18n } from '../../hooks/useI18n.ts';
import { PREVIEW_IFRAME_SANDBOX } from '../../lib/previewIframeSandbox.ts';
import { completeOnboarding } from '../../services/promoterEndpoint.ts';
import { getCurrentStepInfo, getNextStep } from './eventOnboardingFlow.ts';
import { useOnboardingIncompleteEvent } from './hooks/useOnboardingIncompleteEvent.ts';
import { buildPreviewMessage } from './lib/buildPreviewMessage.ts';

export const EventPreview = () => {
  const navigate = useNavigate();
  const { t } = useI18n();

  const currentStep = 'preview';

  const { currentStep: stepNumber, totalSteps, label: heading } = getCurrentStepInfo(currentStep);
  const { data: promoterData } = usePromoterItemQuery();

  const { mutate: publishEvent, isPending: publishEventIsPending } = usePublishEventsMutation();
  const { mutateAsync: completeOnboardingMutate, isPending: onboardingIsPending } = useMutation({
    mutationFn: async () => {
      const result = await completeOnboarding();
      if ('type' in result) {
        throw new Error(result.message);
      }
      return result;
    },
  });

  const { query } = useOnboardingIncompleteEvent({
    onMutationSuccess: () => void navigate(`../${getNextStep(currentStep)}`),
  });

  const { category, targetAudiences, detail } = useEventOptions();
  const { data: venueData } = useVenueItemQuery(query.data?.venue ?? undefined);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const frontendUrl = env.VITE_FRONTEND_URL;

  // Render the iframe at a fixed simulated viewport and scale it down to fit
  // the device frame container. This way the gogigi.com layout always sees
  // the breakpoints it would see on a real device, regardless of how wide
  // the frame ends up being on the promoter's screen.
  const [deviceMode, setDeviceMode] = useState<'desktop' | 'mobile'>('desktop');
  const DESKTOP_WIDTH = 1440;
  const DESKTOP_HEIGHT = 900;
  const MOBILE_WIDTH = 390;
  const MOBILE_HEIGHT = 844;
  const VIEWPORT_WIDTH = deviceMode === 'desktop' ? DESKTOP_WIDTH : MOBILE_WIDTH;

  const [scale, setScale] = useState(1);

  // Callback ref so the ResizeObserver attaches as soon as the screen
  // container mounts (avoids Mantine Box ref-forwarding pitfalls). Re-created
  // whenever the device mode changes so the math uses the current viewport.
  const screenRefCallback = (el: HTMLDivElement | null) => {
    if (!el) return;
    const compute = () => setScale(el.clientWidth / VIEWPORT_WIDTH);
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
  };

  // Hand the form view-model over to the /event-preview iframe via postMessage.
  // We wait for the iframe to send 'preview-ready' (origin-checked against
  // VITE_FRONTEND_URL) and then reply with 'preview-data' targeted at that
  // same origin. Re-runs whenever the underlying data changes.
  useEffect(() => {
    if (!frontendUrl || !query.data) return;

    const message = buildPreviewMessage(query.data, venueData ?? undefined, {
      category: category.data,
      targetAudiences: targetAudiences.data,
      eventDetails: detail.data,
    });
    if (!message) return;

    const post = () => {
      iframeRef.current?.contentWindow?.postMessage(message, frontendUrl);
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== frontendUrl) return;
      if ((event.data as { type?: string } | null)?.type !== 'preview-ready') return;
      post();
    };

    window.addEventListener('message', onMessage);

    // The iframe may already be ready (e.g. on a re-render after data change),
    // so try posting once immediately as well.
    post();

    return () => window.removeEventListener('message', onMessage);
  }, [frontendUrl, query.data, venueData, category.data, targetAudiences.data, detail.data]);

  if (!query.data) {
    return null;
  }

  const publishTheEvents = () => {
    if (!promoterData?.onboarding_event) {
      return;
    }

    const finishOnboarding = async () => {
      try {
        await completeOnboardingMutate();
        void navigate(`../${getNextStep(currentStep)}`);
      } catch {
        notifications.show({
          title: 'Error',
          message: 'Event published but failed to complete onboarding setup.',
          color: 'red',
        });
      }
    };

    publishEvent(
      {
        incompleteEventId: promoterData.onboarding_event,
      },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Event Published Successfully',
            color: 'green',
            message: 'The event has been successfully submitted for review.',
          });

          void finishOnboarding();
        },
        onError: () => {
          notifications.show({
            title: 'Failed to Publish Event',
            message: 'An error occurred while publishing the event. ',
            color: 'red',
          });
        },
      },
    );
  };

  // When VITE_FRONTEND_URL is configured, render the preview step as a
  // full-viewport iframe of the customer-facing event page (via postMessage),
  // bypassing the standard FlowStep panel so the preview matches gogigi.com
  // at full size. Falls back to the legacy in-panel preview otherwise.
  if (frontendUrl) {
    return (
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          background: '#fff',
          // Fill the viewport below whatever site chrome is above us (header).
          // 100dvh accounts for mobile browser chrome.
          height: '100dvh',
        }}
        w="100%"
      >
        <Stack align="center" gap={4} pb={0} pt="md" px="lg" style={{ flexShrink: 0 }}>
          <Text c="grape" fw={500} size="sm">
            {`${t('onboarding_step-label') ?? 'Step'} ${stepNumber} / ${totalSteps}`}
          </Text>
          <Title order={3}>{heading}</Title>
          <Text c="dimmed" size="sm">
            {t('onboarding_preview_subtitle') ?? 'This is how your event will look on gogigi.com'}
          </Text>
          <Box mt="md">
            <SegmentedControl
              data={[
                {
                  value: 'desktop',
                  label: (
                    <Group gap={6} wrap="nowrap">
                      <MonitorIcon size={16} weight="bold" />
                      <span>{t('onboarding_preview_device-desktop') ?? 'Desktop'}</span>
                    </Group>
                  ),
                },
                {
                  value: 'mobile',
                  label: (
                    <Group gap={6} wrap="nowrap">
                      <DeviceMobileIcon size={16} weight="bold" />
                      <span>{t('onboarding_preview_device-mobile') ?? 'Mobile'}</span>
                    </Group>
                  ),
                },
              ]}
              size="sm"
              value={deviceMode}
              onChange={(v) => setDeviceMode(v)}
            />
          </Box>
        </Stack>
        <Box style={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
          <Center style={{ width: '100%', height: '100%', padding: '0 24px 200px' }}>
            {deviceMode === 'desktop' ? (
              <Box
                key="desktop"
                maw={1100}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                w="100%"
              >
                {/* Laptop screen */}
                <Box
                  style={{
                    background: '#1f1f1f',
                    borderRadius: '14px 14px 4px 4px',
                    padding: '22px 16px 16px',
                    boxShadow: '0 30px 60px rgba(0,0,0,0.25)',
                    position: 'relative',
                  }}
                  w="100%"
                >
                  {/* Camera dot */}
                  <Box
                    style={{
                      position: 'absolute',
                      top: 9,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      background: '#444',
                    }}
                  />
                  <div
                    ref={screenRefCallback}
                    style={{
                      width: '100%',
                      aspectRatio: `${DESKTOP_WIDTH} / ${DESKTOP_HEIGHT}`,
                      background: '#fff',
                      borderRadius: 4,
                      overflow: 'hidden',
                      position: 'relative',
                    }}
                  >
                    <iframe
                      ref={iframeRef}
                      sandbox={PREVIEW_IFRAME_SANDBOX}
                      src={`${frontendUrl}/event-preview/`}
                      style={{
                        width: DESKTOP_WIDTH,
                        height: DESKTOP_HEIGHT,
                        border: 0,
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        transformOrigin: 'top left',
                        transform: `scale(${scale})`,
                      }}
                      title="Event preview"
                    />
                  </div>
                </Box>
                {/* Laptop base */}
                <Box
                  style={{
                    width: 'calc(100% + 60px)',
                    maxWidth: '110%',
                    height: 14,
                    background: 'linear-gradient(180deg, #c0c0c0 0%, #8a8a8a 100%)',
                    borderRadius: '0 0 14px 14px',
                    position: 'relative',
                  }}
                >
                  <Box
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 90,
                      height: 5,
                      background: '#6f6f6f',
                      borderRadius: '0 0 6px 6px',
                    }}
                  />
                </Box>
              </Box>
            ) : (
              // Phone frame
              <Box
                key="mobile"
                style={{
                  width: 'min(340px, 80vw)',
                  background: '#1f1f1f',
                  borderRadius: 40,
                  padding: 14,
                  boxShadow: '0 30px 60px rgba(0,0,0,0.25)',
                  position: 'relative',
                }}
              >
                {/* Phone speaker / notch */}
                <Box
                  style={{
                    position: 'absolute',
                    top: 20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 80,
                    height: 18,
                    background: '#000',
                    borderRadius: 12,
                    zIndex: 1,
                  }}
                />
                <div
                  ref={screenRefCallback}
                  style={{
                    width: '100%',
                    aspectRatio: `${MOBILE_WIDTH} / ${MOBILE_HEIGHT}`,
                    background: '#fff',
                    borderRadius: 28,
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <iframe
                    ref={iframeRef}
                    sandbox={PREVIEW_IFRAME_SANDBOX}
                    src={`${frontendUrl}/event-preview/`}
                    style={{
                      width: MOBILE_WIDTH,
                      height: MOBILE_HEIGHT,
                      border: 0,
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      transformOrigin: 'top left',
                      transform: `scale(${scale})`,
                    }}
                    title="Event preview"
                  />
                </div>
              </Box>
            )}
          </Center>
          <Center
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 24,
              pointerEvents: 'none',
              zIndex: 300,
            }}
          >
            <Paper
              bg="gigi-white"
              maw="calc(100% - 32px)"
              px="md"
              py="xs"
              radius={24}
              shadow="xl"
              style={{ pointerEvents: 'auto' }}
              w="fit-content"
            >
              <Group gap="lg" wrap="nowrap">
                <Button intent="secondary" text="Back" type="button" onClick={() => void navigate(-1)} />
                <Button
                  loading={publishEventIsPending || onboardingIsPending}
                  text="Submit for review"
                  type="button"
                  onClick={publishTheEvents}
                />
              </Group>
            </Paper>
          </Center>
        </Box>
      </Box>
    );
  }

  return (
    <FlowStep
      bodyContent={
        <Stack align="center">
          <Box style={{ boxShadow: '0 15px 30px rgba(0,0,0,0.15)' }}>
            <EventPreviewPanel {...query.data} />
          </Box>
          <Box mt={50} w="100%">
            <ButtonRow
              primaryButton={{
                text: 'Submit for review',
                type: 'button',
                isPending: publishEventIsPending || onboardingIsPending,
                onClick: () => publishTheEvents(),
              }}
              secondaryButton={{
                text: 'Back',
                onClick: () => void navigate(-1),
              }}
            />
          </Box>
        </Stack>
      }
      heading={heading}
      progress={{ currentStep: stepNumber, totalSteps }}
    />
  );
};
